import { MapBounds, validMapBounds } from './project-map-inspection';

/**
 * Where a picker restricted to one layer opens (mobile `launcherInitialRegion`, 13933f6bd): the user when they stand
 * at the layer (its bounds grown by 200 m) at zoom 18.5; otherwise the layer's bounds fitted into the map (minus
 * padding), zoom clamped to [15, 19] and to the layer's own minimum. Null without valid bounds. Zoom levels are
 * Web Mercator 256-px levels (the ArcGIS MapView `zoom`).
 */
export interface PickerFrame {
  center: [number, number];
  zoom: number;
  reason: 'user' | 'layer';
}

export const FRAME_MIN_ZOOM = 15;
export const FRAME_MAX_ZOOM = 19;
export const FRAME_USER_ZOOM = 18.5;
export const FRAME_USER_RADIUS_M = 200;
const METRES_PER_DEGREE = 111320;

export interface PickerFrameInput {
  bounds: MapBounds | null | undefined;
  /** The map's size in pixels. */
  view: { width: number; height: number };
  user?: { latitude: number | string | null; longitude: number | string | null } | null;
  layerMinZoom?: number | null;
  padding?: number;
}

export function pickerFrame(input: PickerFrameInput): PickerFrame | null {
  const bounds = input.bounds;
  if (!validMapBounds(bounds)) return null;
  const user = input.user;
  const userLat = Number(user?.latitude);
  const userLon = Number(user?.longitude);
  const hasUser = user && user.latitude !== null && user.latitude !== '' && user.longitude !== null && user.longitude !== ''
    && Number.isFinite(userLat) && Number.isFinite(userLon);
  if (hasUser && withinBounds(bounds, userLat, userLon, FRAME_USER_RADIUS_M)) {
    return { center: [userLon, userLat], zoom: FRAME_USER_ZOOM, reason: 'user' };
  }
  const padding = input.padding ?? 24;
  const width = Math.max(1, (input.view?.width || 0) - 2 * padding);
  const height = Math.max(1, (input.view?.height || 0) - 2 * padding);
  const lonSpan = bounds[2] - bounds[0];
  const ySpan = mercatorY(bounds[3]) - mercatorY(bounds[1]);
  // pixels across = span / full world * 256 * 2^zoom
  const zoomX = lonSpan > 0 ? Math.log2((width * 360) / (256 * lonSpan)) : Infinity;
  const zoomY = ySpan > 0 ? Math.log2((height * 2 * Math.PI) / (256 * ySpan)) : Infinity;
  const floor = Math.max(FRAME_MIN_ZOOM, Number.isFinite(input.layerMinZoom) ? input.layerMinZoom : FRAME_MIN_ZOOM);
  const zoom = Math.min(FRAME_MAX_ZOOM, Math.max(floor, Math.min(zoomX, zoomY)));
  const centerLat = inverseMercatorY((mercatorY(bounds[1]) + mercatorY(bounds[3])) / 2);
  return { center: [(bounds[0] + bounds[2]) / 2, centerLat], zoom, reason: 'layer' };
}

/** True when the point lies within the bounds grown by `marginM` metres. */
export function withinBounds(bounds: MapBounds, latitude: number, longitude: number, marginM = 0): boolean {
  if (!validMapBounds(bounds) || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  const dLat = marginM / METRES_PER_DEGREE;
  const dLon = marginM / (METRES_PER_DEGREE * Math.max(0.01, Math.cos(latitude * Math.PI / 180)));
  return longitude >= bounds[0] - dLon && longitude <= bounds[2] + dLon && latitude >= bounds[1] - dLat && latitude <= bounds[3] + dLat;
}

function mercatorY(latitude: number): number {
  const rad = latitude * Math.PI / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
}

function inverseMercatorY(y: number): number {
  return (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180 / Math.PI;
}
